import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Grid,
  Alert, CircularProgress, Checkbox, Tooltip, InputAdornment, Pagination,
  Select, FormControl, InputLabel, useMediaQuery, useTheme
} from '@mui/material';
import {
  Search, Visibility, CheckCircle, HourglassEmpty, Delete as TrashIcon,
  FiberNew, Download, Send as SendIcon, Add, Upload
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import axios from 'axios';

function FbLeads() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({
    campaign_id: '',
    full_name: '',
    email: '',
    phone: '',
    fb_ad_id: '',
    fb_ad_name: '',
    notes: ''
  });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [filters, setFilters] = useState({ campaign_ids: [], ad_id: '', status: '', search: '', start_date: null, end_date: null });
  const limit = 50;

  useEffect(() => { fetchCampaigns(); }, []);
  useEffect(() => { fetchLeads(); }, [page, filters]);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get('/admin/facebook/campaigns');
      setCampaigns(res.data.data || []);
    } catch (error) { console.error('Error fetching campaigns:', error); }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', (page - 1) * limit);
      if (filters.campaign_ids && filters.campaign_ids.length > 0) params.append('campaign_ids', filters.campaign_ids.join(','));
      if (filters.ad_id) params.append('ad_id', filters.ad_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) {
        const d = filters.start_date; const startStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        params.append('start_date', startStr);
      }
      if (filters.end_date) {
        const e = filters.end_date; const endStr = `${e.getFullYear()}-${String(e.getMonth()+1).padStart(2,'0')}-${String(e.getDate()).padStart(2,'0')}`;
        params.append('end_date', endStr);
      }
      const res = await axios.get(`/admin/facebook/leads?${params.toString()}`);
      setLeads(res.data.data || []);
      setTotal(res.data.total || 0);
      // Extract unique ads for filter
      const uniqueAds = [];
      const adIds = new Set();
      (res.data.data || []).forEach(l => {
        if (l.fb_ad_id && !adIds.has(l.fb_ad_id)) {
          adIds.add(l.fb_ad_id);
          uniqueAds.push({ id: l.fb_ad_id, name: l.fb_ad_name || l.fb_ad_id });
        }
      });
      setAds(uniqueAds);
    } catch (error) { console.error('Error fetching leads:', error); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`/admin/facebook/leads/${leadId}/status`, { status: newStatus });
      setMessage({ type: 'success', text: 'Status updated' });
      fetchLeads();
    } catch (error) { setMessage({ type: 'error', text: 'Error updating status' }); }
  };

  const handleCpaChange = async (leadId, newCpa) => {
    try {
      await axios.put(`/admin/facebook/leads/${leadId}/cpa`, { cpa: newCpa });
      setLeads(leads.map(l => l.id === leadId ? { ...l, cpa: newCpa } : l));
    } catch (error) { setMessage({ type: 'error', text: 'Error updating CPA' }); }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axios.delete(`/admin/facebook/leads/${leadId}`);
      setMessage({ type: 'success', text: 'Lead deleted' });
      fetchLeads();
    } catch (error) { setMessage({ type: 'error', text: 'Error deleting lead' }); }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedLeads.length === 0) return;
    try {
      await axios.put('/admin/facebook/leads/bulk-status', { lead_ids: selectedLeads, status: newStatus });
      setMessage({ type: 'success', text: `${selectedLeads.length} leads updated` });
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) { setMessage({ type: 'error', text: 'Error updating leads' }); }
  };

  const handleBulkSendFeedback = async () => {
    if (selectedLeads.length === 0) return;
    setSendingFeedback(true);
    try {
      const res = await axios.post('/admin/facebook/leads/bulk-feedback', { lead_ids: selectedLeads });
      if (res.data.success) { setMessage({ type: 'success', text: res.data.message }); setSelectedLeads([]); fetchLeads(); }
      else { setMessage({ type: 'error', text: res.data.error || 'Error sending feedback' }); }
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.error || 'Error sending feedback' }); }
    finally { setSendingFeedback(false); }
  };

  const handleAddLead = async () => {
    if (!addFormData.campaign_id || !addFormData.full_name) {
      setMessage({ type: 'error', text: 'Campaign and Name are required' });
      return;
    }
    try {
      await axios.post('/admin/facebook/leads/manual', addFormData);
      setMessage({ type: 'success', text: 'Lead added successfully' });
      setAddDialogOpen(false);
      setAddFormData({ campaign_id: '', full_name: '', email: '', phone: '', fb_ad_id: '', fb_ad_name: '', notes: '' });
      fetchLeads();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.error || 'Error adding lead' }); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      
      // Detect separator (comma or semicolon)
      const firstLine = lines[0];
      const separator = firstLine.includes(';') ? ';' : ',';
      
      const headers = firstLine.split(separator).map(h => h.trim().toLowerCase().replace(/"/g, ''));
      
      const preview = lines.slice(1, 6).map(line => {
        const regex = separator === ';' ? /(".*?"|[^;]+)/g : /(".*?"|[^,]+)/g;
        const values = line.match(regex) || [];
        const row = {};
        headers.forEach((h, i) => {
          row[h] = values[i] ? values[i].replace(/"/g, '').trim() : '';
        });
        return row;
      });
      setImportPreview(preview);
    };
    reader.readAsText(file, 'UTF-8');
  };

   const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      
      // Detect separator (comma or semicolon)
      const firstLine = lines[0];
      const separator = firstLine.includes(';') ? ';' : ',';
      
      const headers = firstLine.split(separator).map(h => h.trim().toLowerCase().replace(/"/g, ''));
      
      const leads = lines.slice(1).map(line => {
        const regex = separator === ';' ? /(".*?"|[^;]+)/g : /(".*?"|[^,]+)/g;
        const values = line.match(regex) || [];
        const row = {};
        headers.forEach((h, i) => {
          row[h] = values[i] ? values[i].replace(/"/g, '').trim() : '';
        });
        return row;
      }).filter(row => row.full_name || row.name || row.nome || row.nome_e_cognome || row['nome e cognome']);

      try {
        const res = await axios.post('/admin/facebook/leads/import', { leads });
        setMessage({ type: 'success', text: `Imported ${res.data.imported} leads` });
        setImportDialogOpen(false);
        setImportFile(null);
        setImportPreview([]);
        fetchLeads();
      } catch (error) {
        setMessage({ type: 'error', text: error.response?.data?.error || 'Error importing leads' });
      }
      setImporting(false);
    };
    reader.readAsText(importFile);
  };

  const handleSelectAll = (e) => setSelectedLeads(e.target.checked ? leads.map(l => l.id) : []);
  const handleSelectLead = (id) => setSelectedLeads(selectedLeads.includes(id) ? selectedLeads.filter(i => i !== id) : [...selectedLeads, id]);
  const openDetail = (lead) => { setSelectedLead(lead); setDetailDialogOpen(true); };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'positive': return <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />;
      case 'pending': return <HourglassEmpty sx={{ color: 'warning.main', fontSize: 18 }} />;
      case 'trash': return <TrashIcon sx={{ color: 'error.main', fontSize: 18 }} />;
      default: return <FiberNew sx={{ color: 'info.main', fontSize: 18 }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) { case 'positive': return 'success'; case 'pending': return 'warning'; case 'trash': return 'error'; default: return 'info'; }
  };

  const getQualityColor = (score) => { if (score >= 70) return 'success'; if (score >= 40) return 'warning'; return 'error'; };

  const exportCSV = () => {
    const headers = ['ID', 'FB Lead ID', 'Date', 'Time', 'Campaign', 'Ad ID', 'Advertiser', 'Name', 'Email', 'Phone', 'Quality Score', 'Status', 'CPA', 'Is Duplicate', 'FB Feedback Sent'];
    const rows = leads.map(l => [l.id, l.fb_lead_id || '', new Date(l.created_at).toLocaleDateString('it-IT'), new Date(l.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }), l.campaign_name || '', l.fb_ad_id || '', l.advertiser_name || '', l.full_name || '', l.email || '', l.phone || '', l.quality_score || 0, l.status || '', l.cpa ? 'Yes' : 'No', l.is_duplicate ? 'Yes' : 'No', l.fb_feedback_sent ? 'Yes' : 'No']);
    const escapeCSV = (field) => { if (field === null || field === undefined) return ''; const str = String(field); if (str.includes(',') || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`; return str; };
    const csv = [headers, ...rows].map(r => r.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `fb_leads_${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Facebook Leads</Typography>
          <Box display="flex" gap={1}>
            <Button variant="contained" startIcon={<Add />} onClick={() => setAddDialogOpen(true)} size={isMobile ? 'small' : 'medium'}>Add Lead</Button>
            <Button variant="contained" startIcon={<Upload />} onClick={() => setImportDialogOpen(true)} size={isMobile ? 'small' : 'medium'}>Import</Button>
            <Button variant="outlined" startIcon={<Download />} onClick={exportCSV} size={isMobile ? 'small' : 'medium'}>Export</Button>
          </Box>
        </Box>

        {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Box display="flex" flexWrap="wrap" gap={{ xs: 1, sm: 2 }} alignItems="center">
              <TextField size="small" placeholder="Search..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} sx={{ minWidth: { xs: '100%', sm: 200 } }} InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20 }} /></InputAdornment> }} />
              {!isMobile && (
                <>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Campaign</InputLabel>
                    <Select 
                      multiple
                      value={filters.campaign_ids || []} 
                      onChange={(e) => setFilters({ ...filters, campaign_ids: e.target.value })} 
                      label="Campaign" 
                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                      renderValue={(selected) => selected.length === 0 ? 'All' : `${selected.length} selected`}
                    >
                      {campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.campaign_name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Ad ID</InputLabel>
                    <Select value={filters.ad_id} onChange={(e) => setFilters({ ...filters, ad_id: e.target.value })} label="Ad ID" MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}>
                      <MenuItem value="">All Ads</MenuItem>
                      {ads.map((a) => <MenuItem key={a.id} value={a.id}>{a.id}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} label="Status">
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="new">New</MenuItem>
                      <MenuItem value="positive">Positive</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="trash">Trash</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}
              {!isTablet && (
                <>
                  <DatePicker label="From" value={filters.start_date} onChange={(v) => setFilters({ ...filters, start_date: v })} slotProps={{ textField: { size: 'small', sx: { width: 140 } } }} />
                  <DatePicker label="To" value={filters.end_date} onChange={(v) => setFilters({ ...filters, end_date: v })} slotProps={{ textField: { size: 'small', sx: { width: 140 } } }} />
                </>
              )}
            <Button variant="text" size="small" onClick={() => setFilters({ campaign_ids: [], ad_id: '', status: '', search: '', start_date: null, end_date: null })}>Reset</Button>
            </Box>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedLeads.length > 0 && (
          <Card sx={{ mb: 2, bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ py: 1, px: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>{selectedLeads.length}</strong> selected</Typography>
                <Button size="small" variant="contained" sx={{ bgcolor: '#10b981', color: 'white', '&:hover': { bgcolor: '#059669' } }} onClick={() => handleBulkStatusChange('positive')}>Positive</Button>
                <Button size="small" variant="contained" sx={{ bgcolor: '#f59e0b', color: 'white', '&:hover': { bgcolor: '#d97706' } }} onClick={() => handleBulkStatusChange('pending')}>Pending</Button>
                <Button size="small" variant="contained" color="error" onClick={() => handleBulkStatusChange('trash')}>Trash</Button>
                {!isMobile && (
                  <Button size="small" variant="contained" sx={{ bgcolor: '#1877F2', '&:hover': { bgcolor: '#166FE5' } }} startIcon={sendingFeedback ? <CircularProgress size={14} color="inherit" /> : <SendIcon />} onClick={handleBulkSendFeedback} disabled={sendingFeedback}>
                    {sendingFeedback ? 'Sending...' : 'Send to FB'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card>
          <TableContainer sx={{ maxHeight: { xs: 400, sm: 600 } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"><Checkbox checked={selectedLeads.length === leads.length && leads.length > 0} indeterminate={selectedLeads.length > 0 && selectedLeads.length < leads.length} onChange={handleSelectAll} /></TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Campaign</TableCell>
                  {!isMobile && <TableCell>Ad ID</TableCell>}
                  {!isMobile && <TableCell>FB Lead ID</TableCell>}
                  <TableCell align="center">Q</TableCell>
                  <TableCell align="center">Status</TableCell>
                  {!isTablet && <TableCell align="center">CPA</TableCell>}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={isMobile ? 5 : 10} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
                ) : leads.length === 0 ? (
                  <TableRow><TableCell colSpan={isMobile ? 5 : 10} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No leads found</Typography></TableCell></TableRow>
                ) : leads.map((lead) => (
                  <TableRow key={lead.id} hover selected={selectedLeads.includes(lead.id)}>
                    <TableCell padding="checkbox"><Checkbox checked={selectedLeads.includes(lead.id)} onChange={() => handleSelectLead(lead.id)} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>{new Date(lead.created_at).toLocaleDateString('it-IT')}</Typography>
                      {!isMobile && <Typography variant="caption" color="text.secondary">{new Date(lead.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, maxWidth: { xs: 80, sm: 150 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.campaign_name || '-'}</Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{lead.fb_ad_id || '-'}</Typography>
                      </TableCell>
                    )}

                    {!isMobile && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>{lead.fb_lead_id || '-'}</Typography>
                      </TableCell>
                    )}
                    <TableCell align="center"><Chip size="small" label={lead.quality_score} color={getQualityColor(lead.quality_score)} sx={{ fontSize: '0.65rem', height: 20 }} /></TableCell>
                    <TableCell align="center"><Tooltip title={lead.status}>{getStatusIcon(lead.status)}</Tooltip></TableCell>
                    {!isTablet && (
                      <TableCell align="center">
                        <Checkbox checked={lead.cpa === true} onChange={(e) => handleCpaChange(lead.id, e.target.checked)} sx={{ p: 0.5, color: 'grey.400', '&.Mui-checked': { color: 'success.main' } }} />
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openDetail(lead)}><Visibility fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteLead(lead.id)}><TrashIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Pagination */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={1}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{total} leads</Typography>
          <Pagination count={Math.ceil(total / limit)} page={page} onChange={(e, v) => setPage(v)} color="primary" size={isMobile ? 'small' : 'medium'} />
        </Box>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Lead #{selectedLead?.id}
            <Chip label={selectedLead?.status} color={getStatusColor(selectedLead?.status)} size="small" sx={{ ml: 1 }} />
          </DialogTitle>
          <DialogContent>
            {selectedLead && (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Campaign</Typography><Typography variant="body2">{selectedLead.campaign_name || '-'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Advertiser</Typography><Typography variant="body2">{selectedLead.advertiser_name || '-'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Ad ID</Typography><Typography variant="body2" sx={{ fontSize: '0.7rem' }}>{selectedLead.fb_ad_id || '-'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Ad Name</Typography><Typography variant="body2" sx={{ fontSize: '0.7rem' }}>{selectedLead.fb_ad_name || '-'}</Typography></Grid>

                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Quality</Typography><Chip label={selectedLead.quality_score} color={getQualityColor(selectedLead.quality_score)} size="small" /></Grid>


                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Created</Typography><Typography variant="body2">{new Date(selectedLead.created_at).toLocaleString('it-IT')}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">FB Lead ID</Typography><Typography variant="body2" sx={{ fontSize: '0.7rem' }}>{selectedLead.fb_lead_id || '-'}</Typography></Grid>

              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            <Button sx={{ bgcolor: '#10b981', color: 'white', '&:hover': { bgcolor: '#059669' } }} variant="contained" size="small" onClick={() => { handleStatusChange(selectedLead.id, 'positive'); setDetailDialogOpen(false); }}>Positive</Button>
            <Button sx={{ bgcolor: '#f59e0b', color: 'white', '&:hover': { bgcolor: '#d97706' } }} variant="contained" size="small" onClick={() => { handleStatusChange(selectedLead.id, 'pending'); setDetailDialogOpen(false); }}>Pending</Button>
            <Button color="error" variant="contained" size="small" onClick={() => { handleStatusChange(selectedLead.id, 'trash'); setDetailDialogOpen(false); }}>Trash</Button>
          </DialogActions>
        </Dialog>

        {/* Add Lead Dialog */}
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Manual Lead</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Campaign</InputLabel>
                <Select value={addFormData.campaign_id} onChange={(e) => setAddFormData({ ...addFormData, campaign_id: e.target.value })} label="Campaign">
                  {campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.campaign_name}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField fullWidth label="Full Name" required value={addFormData.full_name} onChange={(e) => setAddFormData({ ...addFormData, full_name: e.target.value })} />
              <Box display="flex" gap={2}>
                <TextField fullWidth label="Email" value={addFormData.email} onChange={(e) => setAddFormData({ ...addFormData, email: e.target.value })} />
                <TextField fullWidth label="Phone" value={addFormData.phone} onChange={(e) => setAddFormData({ ...addFormData, phone: e.target.value })} />
              </Box>
              <Box display="flex" gap={2}>
                <TextField fullWidth label="Ad ID (optional)" value={addFormData.fb_ad_id} onChange={(e) => setAddFormData({ ...addFormData, fb_ad_id: e.target.value })} />
                <TextField fullWidth label="Ad Name (optional)" value={addFormData.fb_ad_name} onChange={(e) => setAddFormData({ ...addFormData, fb_ad_name: e.target.value })} />
              </Box>
              <TextField fullWidth label="Notes" multiline rows={2} value={addFormData.notes} onChange={(e) => setAddFormData({ ...addFormData, notes: e.target.value })} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleAddLead}>Add Lead</Button>
          </DialogActions>
        </Dialog>
     {/* Import CSV Dialog */}
        <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Import Leads from CSV</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                CSV must have headers. Supports Facebook Lead Ads export format. Required: full_name or name.
              </Alert>
              <Button variant="outlined" component="label" fullWidth sx={{ py: 2, mb: 2 }}>
                {importFile ? importFile.name : 'Select CSV File'}
                <input type="file" hidden accept=".csv" onChange={handleFileSelect} />
              </Button>
              {importPreview.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview (first 5 rows):</Typography>
                  <TableContainer sx={{ maxHeight: 200 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {Object.keys(importPreview[0]).map(h => <TableCell key={h} sx={{ fontSize: '0.7rem' }}>{h}</TableCell>)}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importPreview.map((row, i) => (
                          <TableRow key={i}>
                            {Object.values(row).map((v, j) => <TableCell key={j} sx={{ fontSize: '0.7rem' }}>{v}</TableCell>)}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setImportDialogOpen(false); setImportFile(null); setImportPreview([]); }}>Cancel</Button>
            <Button variant="contained" onClick={handleImport} disabled={!importFile || importing}>
              {importing ? <CircularProgress size={20} /> : `Import ${importPreview.length > 0 ? 'Leads' : ''}`}
            </Button>
          </DialogActions>
        </Dialog>

 </Box>
    </LocalizationProvider>
  );
}

export default FbLeads;
